import React, { useState, useEffect, useCallback, useRef } from 'react';
import Calendar from './components/Calendar';
import Login from './components/Login';
import { API_ENDPOINTS, apiCall } from './config/api';
import { DateOnly } from './utils/DateOnly';
import { buildUrlSearch, readWeekFromUrl, readFiltersFromUrl, getMondayOf } from './utils/urlState';
import './App.css';
import logger from './utils/logger';
import { useAuth } from './auth/AuthProvider';

/**
 * Compute the Monday of the week containing the given date.
 * @param {Date} date
 * @returns {Date}
 */
function getMonday(date) {
  const d = new Date(date);
  const dayOfWeek = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - dayOfWeek);
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Format a Date as YYYY-MM-DD.
 */
function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Given a center date, return the start (Monday of prev week) and end
 * (Sunday of next week) for a 3-week fetch window.
 */
function computeLoadRange(centerDate) {
  const monday = getMonday(centerDate);
  const start = new Date(monday);
  start.setDate(start.getDate() - 7); // previous week's Monday
  const end = new Date(monday);
  end.setDate(end.getDate() + 13); // next week's Sunday
  return { start: formatDate(start), end: formatDate(end) };
}

function App() {
  const { isAuthenticated, currentUser, loading: authLoading, logout } = useAuth();
  const [workouts, setWorkouts] = useState([]);

  const [error, setError] = useState(null);
  const [triClubSchedule, setTriClubSchedule] = useState(null);
  const [version, setVersion] = useState(null);
  const [locations, setLocations] = useState([]);

  // URL-driven state: which week and which filters the calendar shows.
  // calendarKey is incremented on popstate so Calendar remounts with new initial values.
  const [calendarWeek, setCalendarWeek] = useState(() => readWeekFromUrl());
  const [calendarFilters, setCalendarFilters] = useState(() => readFiltersFromUrl());
  const [calendarKey, setCalendarKey] = useState(0);

  // Track the date range that is currently loaded
  const loadedRangeRef = useRef(null);

  // Transform raw API workout data into the format used by the frontend
  const transformWorkout = useCallback((workout) => {
    const displayDate = workout.selection?.currentPlanDay || workout.originallyPlannedDay;
    const [year, month, day] = displayDate.split('-').map(Number);
    return {
      id: workout.id,
      title: workout.title,
      workoutType: workout.workoutType,
      workoutDescription: workout.workoutDescription,
      plannedDuration: workout.plannedDuration,
      plannedDistanceInMeters: workout.plannedDistanceInMeters,
      tss: workout.tss,
      originallyPlannedDay: workout.originallyPlannedDay,
      currentPlanDay: workout.selection?.currentPlanDay,
      workoutDate: new DateOnly(year, month, day),
      coachComments: workout.coachComments,
      isSelected: workout.selection ? workout.selection.isSelected : true,
      timeOfDay: workout.selection?.timeOfDay,
      workoutLocation: workout.selection?.workoutLocation,
      heatTraining: workout.selection?.heatTraining || false,
      isCustom: workout.isCustom || false,
    };
  }, []);

  // Fetch workouts for a date range. If no range given, fetches 3 weeks around today.
  const loadWorkouts = useCallback(async (start, end) => {
    try {
      let url = API_ENDPOINTS.WORKOUTS;
      if (start && end) {
        url += `?start=${start}&end=${end}`;
      }
      logger.log('Fetching workouts from:', url);
      const response = await apiCall(url, { timeout: 15000 });
      logger.log('Response status:', response.status);
      if (!response.ok) {
        throw new Error(`Failed to load workouts: ${response.status}`);
      }
      const data = await response.json();
      logger.log('Number of workouts:', data.workouts?.length);

      const transformedWorkouts = data.workouts.map(transformWorkout);

      logger.log('Transformed workouts:', transformedWorkouts.length);
      setWorkouts(transformedWorkouts);
      if (start && end) {
        loadedRangeRef.current = { start, end };
      }
      setError(null);
    } catch (err) {
      let errorMessage;

      if (err.name === 'AbortError') {
        errorMessage = 'Request timed out loading workouts. Please refresh the page to try again.';
      } else if (err instanceof TypeError && err.message.includes('fetch')) {
        errorMessage = `Cannot connect to backend server at ${API_ENDPOINTS.WORKOUTS}. Make sure the backend is running.`;
      } else if (err.message.includes('Failed to load workouts')) {
        errorMessage = `${err.message}. The backend server responded with an error.`;
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }

      setError(errorMessage);
      logger.error('Error loading workouts:', err);
      logger.error('Error details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
    }
  }, [transformWorkout]);

  // Load workouts when authenticated — fetch 3-week window around the URL's week (or today).
  // Defines the fetch inline to avoid the set-state-in-effect rule tracing through
  // the loadWorkouts useCallback (which calls setWorkouts internally).
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const { start, end } = computeLoadRange(calendarWeek);

    const fetchInitialWorkouts = async () => {
      try {
        let url = API_ENDPOINTS.WORKOUTS;
        if (start && end) {
          url += `?start=${start}&end=${end}`;
        }
        const response = await apiCall(url, { timeout: 15000 });
        if (!response.ok) {
          throw new Error(`Failed to load workouts: ${response.status}`);
        }
        const data = await response.json();
        const transformedWorkouts = data.workouts.map(transformWorkout);
        setWorkouts(transformedWorkouts);
        loadedRangeRef.current = { start, end };
        setError(null);
      } catch (err) {
        let errorMessage;
        if (err.name === 'AbortError') {
          errorMessage = 'Request timed out loading workouts. Please refresh the page to try again.';
        } else if (err instanceof TypeError && err.message.includes('fetch')) {
          errorMessage = `Cannot connect to backend server at ${API_ENDPOINTS.WORKOUTS}. Make sure the backend is running.`;
        } else if (err.message.includes('Failed to load workouts')) {
          errorMessage = `${err.message}. The backend server responded with an error.`;
        } else {
          errorMessage = err.message || 'Unknown error occurred';
        }
        setError(errorMessage);
        logger.error('Error loading workouts:', err);
      }
    };

    fetchInitialWorkouts();
    // calendarWeek is intentionally excluded from deps: we only want to run this once on
    // auth change, not on every week navigation (handleWeekChange triggers its own fetches).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, transformWorkout]);

  // Push a new URL state whenever week or filters change
  const pushUrlState = useCallback((weekDate, filters) => {
    const search = buildUrlSearch(getMondayOf(weekDate), filters);
    const newUrl = `${window.location.pathname}${search}`;
    if (newUrl !== `${window.location.pathname}${window.location.search}`) {
      window.history.pushState({ week: weekDate.toISOString(), filters }, '', newUrl);
    }
  }, []);

  // Handle browser back/forward
  useEffect(() => {
    const onPopState = () => {
      setCalendarWeek(readWeekFromUrl());
      setCalendarFilters(readFiltersFromUrl());
      setCalendarKey(k => k + 1);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  // Called by Calendar when the user navigates to a different week
  const handleWeekChange = useCallback((newCurrentDate) => {
    const monday = getMonday(newCurrentDate);
    const mondayStr = formatDate(monday);
    const sunday = new Date(monday);
    sunday.setDate(sunday.getDate() + 6);
    const sundayStr = formatDate(sunday);

    // Update URL to reflect the new week
    setCalendarWeek(monday);
    pushUrlState(monday, calendarFilters);

    const range = loadedRangeRef.current;
    if (range && mondayStr >= range.start && sundayStr <= range.end) {
      // The new week is within the loaded buffer — no fetch needed
      return;
    }

    // Fetch a new 3-week window centered on the navigated-to week.
    // Update the range optimistically so rapid clicks don't trigger
    // duplicate fetches while the first one is still in flight.
    const { start, end } = computeLoadRange(newCurrentDate);
    loadedRangeRef.current = { start, end };
    loadWorkouts(start, end);
  }, [loadWorkouts, pushUrlState, calendarFilters]);

  // Load tri club schedule
  useEffect(() => {
    if (!isAuthenticated) {
      return; // Don't load schedule if not authenticated
    }

    const loadTriClubSchedule = async () => {
      try {
        logger.log('Fetching tri club schedule from:', API_ENDPOINTS.TRI_CLUB_SCHEDULE);
        const response = await apiCall(API_ENDPOINTS.TRI_CLUB_SCHEDULE);
        if (!response.ok) {
        logger.warn('Failed to load tri club schedule:', response.status);
          return; // Silently fail - schedule is optional
        }
        const data = await response.json();
        logger.log('Received tri club schedule:', data);
        setTriClubSchedule(data);
      } catch (err) {
        logger.warn('Error loading tri club schedule:', err);
        // Silently fail - schedule is optional
      }
    };

    loadTriClubSchedule();
  }, [isAuthenticated]);

  // Load travel locations
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const loadLocations = async () => {
      try {
        const response = await apiCall(API_ENDPOINTS.LOCATIONS);
        if (!response.ok) {
          logger.warn('Failed to load locations:', response.status);
          return;
        }
        const data = await response.json();
        setLocations(data);
      } catch (err) {
        logger.warn('Error loading locations:', err);
      }
    };

    loadLocations();
  }, [isAuthenticated]);

  // Load application version
  useEffect(() => {
    const loadVersion = async () => {
      try {
        const response = await apiCall(API_ENDPOINTS.VERSION);
        if (!response.ok) {
          setVersion('unknown');
          return;
        }
        const data = await response.json();
        setVersion(data.commit_hash || 'unknown');
      } catch (err) {
        logger.warn('Failed to load version:', err);
        setVersion('unknown');
      }
    };

    loadVersion();
  }, []);

  // Handle workout selection toggle
  const handleWorkoutSelection = async (workoutId, isSelected) => {
    try {
      const response = await apiCall(API_ENDPOINTS.SELECTIONS(workoutId), {
        method: 'PUT',
        body: JSON.stringify({ isSelected }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update selection: ${response.status}`);
      }

      // Update local state - when deselecting, also clear timeOfDay
      setWorkouts(prevWorkouts =>
        prevWorkouts.map(workout =>
          workout.id === workoutId 
            ? { ...workout, isSelected, timeOfDay: isSelected ? workout.timeOfDay : null } 
            : workout
        )
      );
    } catch (err) {
      logger.error('Error updating workout selection:', err);
      alert('Failed to update workout selection. Please try again.');
    }
  };

  // Handle workout date change (drag and drop)
  const handleWorkoutDateChange = async (workoutId, newDate) => {
    try {
      // newDate is a Date object, convert to YYYY-MM-DD format
      const dateStr = `${newDate.getFullYear()}-${String(newDate.getMonth() + 1).padStart(2, '0')}-${String(newDate.getDate()).padStart(2, '0')}`;
      
      // Use selections endpoint for all workouts
      const response = await apiCall(API_ENDPOINTS.SELECTIONS(workoutId), {
        method: 'PUT',
        body: JSON.stringify({ currentPlanDay: dateStr }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update workout date: ${response.status}`);
      }

      // Update local state
      setWorkouts(prevWorkouts =>
        prevWorkouts.map(workout => {
          if (workout.id === workoutId) {
            return {
              ...workout,
              currentPlanDay: dateStr,
              workoutDate: new DateOnly(newDate.getFullYear(), newDate.getMonth() + 1, newDate.getDate())
            };
          }
          return workout;
        })
      );
    } catch (err) {
      logger.error('Error updating workout date:', err);
      alert('Failed to move workout. Please try again.');
    }
  };

  // Handle workout time of day change (drag and drop to time slot)
  const handleWorkoutTimeOfDayChange = async (workoutId, timeOfDay) => {
    try {
      // Use selections endpoint for all workouts
      const response = await apiCall(API_ENDPOINTS.SELECTIONS(workoutId), {
        method: 'PUT',
        body: JSON.stringify({ timeOfDay: timeOfDay === 'unscheduled' ? null : timeOfDay }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update workout time: ${response.status}`);
      }

      // Update local state
      setWorkouts(prevWorkouts =>
        prevWorkouts.map(workout => {
          if (workout.id === workoutId) {
            return {
              ...workout,
              timeOfDay: timeOfDay === 'unscheduled' ? null : timeOfDay
            };
          }
          return workout;
        })
      );
    } catch (err) {
      logger.error('Error updating workout time:', err);
      alert('Failed to update workout time. Please try again.');
    }
  };

  // Handle combined selection updates (date and/or time)
  const handleWorkoutSelectionUpdate = async (workoutId, updateFields) => {
    try {
      const body = { ...updateFields };
      // Convert unscheduled string to null to match API expectations
      if (body.timeOfDay === 'unscheduled') {
        body.timeOfDay = null;
      }

      const response = await apiCall(API_ENDPOINTS.SELECTIONS(workoutId), {
        method: 'PUT',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        throw new Error(`Failed to update selection: ${response.status}`);
      }

      // Update local state - apply any fields provided
      setWorkouts(prevWorkouts =>
        prevWorkouts.map(workout => {
          if (workout.id === workoutId) {
            const updated = { ...workout };
            if (body.currentPlanDay) {
              updated.currentPlanDay = body.currentPlanDay;
              const [y, m, d] = body.currentPlanDay.split('-').map(Number);
              updated.workoutDate = new DateOnly(y, m, d);
            }
            if (Object.prototype.hasOwnProperty.call(body, 'timeOfDay')) {
              updated.timeOfDay = body.timeOfDay;
            }
            return updated;
          }
          return workout;
        })
      );
    } catch (err) {
      logger.error('Error updating workout selection:', err);
      alert('Failed to update workout selection. Please try again.');
    }
  };

  // Handle workout location change
  const handleWorkoutLocationChange = async (workoutId, workoutLocation) => {
    try {
      const response = await apiCall(API_ENDPOINTS.SELECTIONS(workoutId), {
        method: 'PUT',
        body: JSON.stringify({ workoutLocation }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update workout location: ${response.status}`);
      }

      // Update local state
      setWorkouts(prevWorkouts =>
        prevWorkouts.map(workout => {
          if (workout.id === workoutId) {
            return {
              ...workout,
              workoutLocation
            };
          }
          return workout;
        })
      );
    } catch (err) {
      logger.error('Error updating workout location:', err);
      alert('Failed to update workout location. Please try again.');
    }
  };

  // Handle heat training toggle for a workout (bike/run only)
  const handleWorkoutHeatTrainingChange = async (workoutId, heatTraining) => {
    try {
      const response = await apiCall(API_ENDPOINTS.SELECTIONS(workoutId), {
        method: 'PUT',
        body: JSON.stringify({ heatTraining }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update heat training: ${response.status}`);
      }

      // Update local state
      setWorkouts(prevWorkouts =>
        prevWorkouts.map(workout =>
          workout.id === workoutId ? { ...workout, heatTraining } : workout
        )
      );
    } catch (err) {
      logger.error('Error updating heat training:', err);
      alert('Failed to update heat training. Please try again.');
    }
  };

  // Called by Calendar when the user changes source filters
  const handleFiltersChange = useCallback((newFilters) => {
    setCalendarFilters(newFilters);
    pushUrlState(calendarWeek, newFilters);
  }, [pushUrlState, calendarWeek]);

  // Handle new location added
  const handleLocationAdded = (newLocation) => {
    setLocations(prev => [...prev, newLocation]);
  };

  // Handle export to calendar
  const handleExportToCalendar = async (dateRange) => {
    try {
      const response = await apiCall(API_ENDPOINTS.EXPORT_TO_CALENDAR, {
        method: 'POST',
        body: JSON.stringify(dateRange),
        timeout: 30000, // 30 seconds for CalDAV operations (deleting + creating events can take time)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to export: ${response.status}`);
      }

      const result = await response.json();
      // If the backend returned per-day results, present successes and failures to the user
      if (result.results && Array.isArray(result.results) && result.results.length > 0) {
        const successes = result.results.filter(r => r.success);
        const failures = result.results.filter(r => !r.success);
        let msg = `Export results for ${result.dateRange?.start || ''} to ${result.dateRange?.end || ''}:\n\n`;
        if (successes.length > 0) {
          msg += `Successfully uploaded (${successes.length}):\n`;
          successes.forEach(s => {
            msg += `- ${s.date}\n`;
          });
          msg += '\n';
        }
        if (failures.length > 0) {
          msg += `Failed to upload (${failures.length}):\n`;
          failures.forEach(f => {
            msg += `- ${f.date}: ${f.error || 'Unknown error'}\n`;
          });
        }
        alert(msg);
      } else {
        alert(`Successfully exported ${result.eventsCreated} workout events to Apple Calendar!`);
      }
    } catch (err) {
      logger.error('Error exporting to calendar:', err);
      throw err; // Re-throw so the modal can display the error
    }
  };

  // Handle adding custom workout
  const handleAddCustomWorkout = async (workoutData) => {
    try {
      const apiData = {
        title: workoutData.title,
        workoutType: workoutData.workoutType,
        workoutDescription: workoutData.workoutDescription,
        originallyPlannedDay: workoutData.originallyPlannedDay,
        plannedDuration: workoutData.plannedDuration,
        plannedDistanceInMeters: workoutData.plannedDistanceInMeters,
        tss: workoutData.tss,
        timeOfDay: workoutData.timeOfDay,
        workoutLocation: workoutData.workoutLocation,
        isCustom: true,
        // Recurring fields (only sent when isRecurring is true)
        ...(workoutData.isRecurring && {
          isRecurring: true,
          recurrenceDaysOfWeek: workoutData.recurrenceDaysOfWeek,
          recurrenceEndDate: workoutData.recurrenceEndDate || null,
        }),
      };

      const response = await apiCall(API_ENDPOINTS.WORKOUTS, {
        method: 'POST',
        body: JSON.stringify(apiData),
      });

      if (!response.ok) {
        throw new Error(`Failed to create custom workout: ${response.status}`);
      }

      const result = await response.json();

      const transformOne = (w) => {
        const displayDate = w.selection?.currentPlanDay || w.originallyPlannedDay;
        const [year, month, day] = displayDate.split('-').map(Number);
        return {
          id: w.id,
          title: w.title,
          workoutType: w.workoutType,
          workoutDescription: w.workoutDescription,
          plannedDuration: w.plannedDuration,
          plannedDistanceInMeters: w.plannedDistanceInMeters,
          tss: w.tss,
          originallyPlannedDay: w.originallyPlannedDay,
          currentPlanDay: w.selection?.currentPlanDay,
          workoutDate: new DateOnly(year, month, day),
          isSelected: true,
          timeOfDay: w.selection?.timeOfDay,
          workoutLocation: w.selection?.workoutLocation,
          heatTraining: w.selection?.heatTraining || false,
          isCustom: true,
          recurringGroupId: w.recurringGroupId || null,
        };
      };

      if (result.workouts) {
        // Recurring: server returned array
        setWorkouts(prev => [...prev, ...result.workouts.map(transformOne)]);
      } else {
        // Single workout
        setWorkouts(prev => [...prev, transformOne(result)]);
      }
    } catch (err) {
      logger.error('Error creating custom workout:', err);
      alert('Failed to create custom workout. Please try again.');
    }
  };

  // Handle importing workouts from CSV file
  const handleImportWorkouts = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiCall(API_ENDPOINTS.IMPORT_WORKOUTS, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - let browser set it with boundary for FormData
        headers: {},
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Failed to import workouts: ${response.status}`);
      }

      const result = await response.json();
      
      // Reload workouts for the current date range
      const range = loadedRangeRef.current;
      if (range) {
        await loadWorkouts(range.start, range.end);
      } else {
        await loadWorkouts();
      }
      
      return result;
    } catch (err) {
      logger.error('Error importing workouts:', err);
      throw err;
    }
  };


  // Show loading indicator while checking authentication
  if (authLoading) {
    return (
      <div className="App">
        <main className="App-main">
          <p>Loading…</p>
        </main>
      </div>
    );
  }

  // Show login page if not authenticated
  if (!isAuthenticated) {
    return <Login />;
  }

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <div>
            <h1>Workout Planner</h1>
            <p>Plan your workouts from TrainingPeaks</p>
          </div>
          <div className="header-user">
            <div>
              <p>Welcome, {currentUser?.username}!</p>
              {version && <p className="version">version {version}</p>}
            </div>
            <button onClick={logout} className="logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="App-main">
        {error && <p className="error">Error: {error}</p>}
        {!error && (
          <>
            <Calendar
              key={calendarKey}
              workouts={workouts}
              triClubSchedule={triClubSchedule}
              initialDate={new DateOnly(
                calendarWeek.getFullYear(),
                calendarWeek.getMonth() + 1,
                calendarWeek.getDate()
              )}
              initialFilters={calendarFilters}
              onWorkoutSelectionToggle={handleWorkoutSelection}
              onWorkoutDateChange={handleWorkoutDateChange}
              onWorkoutTimeOfDayChange={handleWorkoutTimeOfDayChange}
              onWorkoutSelectionUpdate={handleWorkoutSelectionUpdate}
              onWorkoutLocationChange={handleWorkoutLocationChange}
              onWorkoutHeatTrainingChange={handleWorkoutHeatTrainingChange}
              onExportToCalendar={handleExportToCalendar}
              onAddCustomWorkout={handleAddCustomWorkout}
              onImportWorkouts={handleImportWorkouts}
              onEditWorkout={() => {
                const range = loadedRangeRef.current;
                return range ? loadWorkouts(range.start, range.end) : loadWorkouts();
              }}
              onWeekChange={handleWeekChange}
              onFiltersChange={handleFiltersChange}
              locations={locations}
              onLocationAdded={handleLocationAdded}
            />
          </>
        )}
      </main>
    </div>
  );
}

export default App;
